import { useI18n } from "@/i18n/i18n";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Clock } from "lucide-react";
import TimeSelect from "@/components/TimeSelect";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

/**
 * Resolve the effective (start, end) time for a given day.
 * Falls back to the group's default times when no override exists.
 */
export const resolveDayTimes = (day, defaultStart, defaultEnd, timesByDay) => {
  const override = timesByDay?.[day];
  if (override && (override.start || override.end)) {
    return {
      start: override.start || defaultStart || "",
      end: override.end || defaultEnd || "",
    };
  }
  return { start: defaultStart || "", end: defaultEnd || "" };
};

/**
 * Returns true when at least one day in `days` has a real override
 * (different start OR end from the group's defaults).
 */
export const hasPerDayOverrides = (days, defaultStart, defaultEnd, timesByDay) => {
  if (!timesByDay || typeof timesByDay !== "object") return false;
  for (const d of days || []) {
    const o = timesByDay[d];
    if (!o) continue;
    if ((o.start && o.start !== defaultStart) || (o.end && o.end !== defaultEnd)) return true;
  }
  return false;
};

export const buildScheduleLabel = (days, start, end, t, timesByDay = null) => {
  if (!days?.length && !start && !end) return "";
  // Per-day mode: only when there are real overrides among the selected days
  if (hasPerDayOverrides(days, start, end, timesByDay)) {
    const parts = (days || []).map((d) => {
      const { start: s } = resolveDayTimes(d, start, end, timesByDay);
      return s ? `${t(`day_${d}`)} ${s}` : t(`day_${d}`);
    });
    return parts.join(" · ");
  }
  const dayStr = (days || []).map((d) => t(`day_${d}`)).join(" · ");
  const timeStr = start && end ? `${start}–${end}` : start ? start : "";
  return [dayStr, timeStr].filter(Boolean).join(" · ");
};

export default function ScheduleEditor({
  days = [],
  startTime = "",
  endTime = "",
  timesByDay = null,
  onChange,
  testIdPrefix = "schedule",
}) {
  const { t } = useI18n();

  // The per-day panel is opened either explicitly (user toggle) or
  // implicitly (any existing override was loaded from DB).
  const perDayActive = !!timesByDay && Object.keys(timesByDay).length > 0;

  const emit = (patch) => {
    onChange({ days, startTime, endTime, timesByDay, ...patch });
  };

  const toggleDay = (d) => {
    const next = days.includes(d) ? days.filter((x) => x !== d) : [...days, d];
    next.sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
    // If day is being removed, also drop its per-day override
    let nextTimes = timesByDay;
    if (!next.includes(d) && timesByDay && timesByDay[d]) {
      nextTimes = { ...timesByDay };
      delete nextTimes[d];
      if (Object.keys(nextTimes).length === 0) nextTimes = null;
    }
    emit({ days: next, timesByDay: nextTimes });
  };

  const setPerDayEnabled = (on) => {
    if (on) {
      // Pre-fill each selected day with the group default so the admin
      // only edits the ones that actually differ.
      const seed = {};
      for (const d of days || []) {
        seed[d] = { start: startTime || "", end: endTime || "" };
      }
      emit({ timesByDay: seed });
    } else {
      emit({ timesByDay: null });
    }
  };

  const updateDayTime = (d, field, value) => {
    const next = { ...(timesByDay || {}) };
    next[d] = { ...(next[d] || {}), [field]: value };
    emit({ timesByDay: next });
  };

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-3 bg-slate-50">
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">{t("schedule_days")}</Label>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {DAYS.map((d) => {
            const selected = days.includes(d);
            return (
              <button
                key={d}
                type="button"
                data-testid={`${testIdPrefix}-day-${d}`}
                onClick={() => toggleDay(d)}
                className={`h-9 w-11 rounded-md text-xs font-medium uppercase tracking-wide border transition-all ${
                  selected
                    ? "bg-brand-blue text-white border-brand-blue shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:border-brand-blue/40 hover:text-brand-blue"
                }`}
              >
                {t(`day_${d}`)}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {t("start_time")}
          </Label>
          <div className="mt-1.5">
            <TimeSelect
              value={startTime}
              onChange={(v) => emit({ startTime: v })}
              testId={`${testIdPrefix}-start-time`}
            />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {t("end_time")}
          </Label>
          <div className="mt-1.5">
            <TimeSelect
              value={endTime}
              onChange={(v) => emit({ endTime: v })}
              testId={`${testIdPrefix}-end-time`}
            />
          </div>
        </div>
      </div>

      {/* Per-day time overrides — appears once 2+ days are selected. */}
      {days.length >= 2 && (
        <div className="pt-2 border-t border-slate-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <Label htmlFor={`${testIdPrefix}-per-day-switch`} className="text-xs">
                {t("per_day_times")}
              </Label>
              <p className="text-[11px] text-slate-500 mt-0.5">{t("per_day_times_hint")}</p>
            </div>
            <Switch
              id={`${testIdPrefix}-per-day-switch`}
              data-testid={`${testIdPrefix}-per-day-switch`}
              checked={perDayActive}
              onCheckedChange={setPerDayEnabled}
            />
          </div>

          {perDayActive && (
            <div className="mt-3 space-y-2" data-testid={`${testIdPrefix}-per-day-list`}>
              {days.map((d) => {
                const val = timesByDay?.[d] || { start: startTime, end: endTime };
                return (
                  <div
                    key={d}
                    className="grid grid-cols-[64px_1fr_1fr] gap-2 items-center"
                    data-testid={`${testIdPrefix}-per-day-row-${d}`}
                  >
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
                      {t(`day_${d}`)}
                    </span>
                    <TimeSelect
                      value={val.start || ""}
                      onChange={(v) => updateDayTime(d, "start", v)}
                      testId={`${testIdPrefix}-per-day-start-${d}`}
                    />
                    <TimeSelect
                      value={val.end || ""}
                      onChange={(v) => updateDayTime(d, "end", v)}
                      testId={`${testIdPrefix}-per-day-end-${d}`}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
